import React, { useState } from 'react';
import { Integration } from '../../../types/integrations';

interface IntegrationSelectionModalProps {
  isOpen: boolean;
  availableIntegrations: Integration[];
  onClose: () => void;
  onSelectIntegration: (integration: Integration) => Promise<void>;
  isLoading: boolean;
}

const IntegrationSelectionModal: React.FC<IntegrationSelectionModalProps> = ({
  isOpen,
  availableIntegrations,
  onClose,
  onSelectIntegration,
  isLoading
}) => {
  const [error, setError] = useState<string | null>(null);

  const handleIntegrationSelect = async (integration: Integration) => {
    try {
      setError(null);
      await onSelectIntegration(integration);
    } catch (err: any) {
      setError(err.message || 'Failed to create integration');
    }
  };

  if (!isOpen) return null;
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">Select Integration Type</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          

          
          {/* Error Display */}
          {error && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-sm font-medium">{error}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-3">
            {availableIntegrations.map((integration) => (
                              <div
                  key={integration.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => handleIntegrationSelect(integration)}
                >
                <div className="flex flex-col items-center text-center">
                  {/* Integration Icon */}
                  {integration.icon_url ? (
                    <div className="w-12 h-12 mb-2 flex items-center justify-center overflow-hidden bg-gray-50 rounded-lg border border-gray-200">
                      <img
                        src={integration.icon_url}
                        alt={integration.display_name || integration.name}
                        className="w-full h-full object-contain p-1 transition-transform duration-200 group-hover:scale-105"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          objectPosition: 'center'
                        }}
                        onError={(e) => {
                          // Fallback to text if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    </div>
                  ) : null}

                  {/* Fallback Icon */}
                  <div className={`w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg transition-transform duration-200 hover:scale-105 ${integration.icon_url ? 'hidden' : ''}`}>
                    {integration.display_name?.charAt(0) || integration.name.charAt(0) || 'I'}
                  </div>

                  {/* Integration Name */}
                  <div className="text-sm font-medium text-gray-900 transition-colors duration-200 hover:text-blue-600">
                    {integration.display_name || integration.name.replace(/-/g, ' ')}
                  </div>
                  
                  {/* Integration Description */}
                  <p className="text-xs text-gray-500 mt-1">
                    {integration.description || 'Connect your account'}
                  </p>
                </div>
                
                {integration.name === 'quickbooks-online' && (
                  <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                    ⚠️ Under development
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationSelectionModal;
